import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'
import { FormProvider } from 'react-hook-form'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  DynamicForm,
  useDynamicForm,
} from '~/client/src/shared/components/DynamicForm'
import {
  DYNAMIC_FORM_ID,
  FieldType,
} from '~/client/src/shared/components/DynamicForm/types'
import { FormListSelector } from '~/client/src/shared/components/FormListSelector'
import { useResolvedTemplate } from '~/client/src/shared/components/FormListSelector/useResolvedTemplate'
import { FormPropertiesSidebar } from '~/client/src/shared/components/FormPropertiesSidebar'
import { ChecklistIcon } from '~/client/src/shared/components/icons/ChecklistIcon'
import { ChevronDownIcon } from '~/client/src/shared/components/icons/ChevronDownIcon'
import { Loader } from '~/client/src/shared/components/Loader'
import ResourceEditFormSelector from '~/client/src/shared/components/ResourceViewEdit/ResourceEditFormSelector'
import { useDeriveFieldUpdates } from '~/client/src/shared/components/ResourceViewEdit/useDeriveFieldUpdates'
import { useResourceFields } from '~/client/src/shared/components/ResourceViewEdit/useResourceFields'
import { useResourceFileUpload } from '~/client/src/shared/components/ResourceViewEdit/useResourceFileUpload'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import {
  getResourceTypeLabels,
  getSelectFormTitle,
} from '~/client/src/shared/constants/resourceTypeLabels'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import UserProject from '~/client/src/shared/models/UserProject'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'

import { useResourcePickerContext } from '../ResourcePickerContext'

const PickerCreationForm: React.FC<{ resourceSchema: ResourceSchema }> = ({
  resourceSchema,
}) => {
  const ctx = useResourcePickerContext()
  const {
    mode,
    projectId,
    resourceTypeId,
    onCreateResource,
    onSelect,
    selectedIds,
    addCreatedItem,
    stopCreating,
    allowSelectExisting,
    templateAllowList,
    creationRootDefaults,
    creationFieldOptions,
  } = ctx

  const stores = React.useContext(MobXProviderContext) as any
  const isAdmin = UserProject.isAdmin(
    stores.state?.user,
    stores.userProjectsStore,
  )

  const [chosenTemplateId, setChosenTemplateId] = React.useState<string | null>(
    null,
  )
  const [isCreatingNewForm, setIsCreatingNewForm] = React.useState(false)
  const [isSwappingTemplate, setIsSwappingTemplate] = React.useState(false)
  const [isPropertiesExpanded, setIsPropertiesExpanded] = React.useState(true)
  const sectionLabel = getResourceTypeLabels(resourceTypeId).sectionLabel()

  const {
    loading: templatesLoading,
    templates,
    effectiveTemplateId,
    effectiveTemplate: template,
    needsSelection,
  } = useResolvedTemplate({
    enabled: !!projectId && !!resourceTypeId,
    chosenTemplateId,
    templateAllowList,
    templates: resourceSchema.templates,
    loading: resourceSchema.loading,
  })

  const propertyDefinitions = resourceSchema.properties

  const { fields, updateField } = useResourceFields({
    record: creationRootDefaults ?? null,
    template,
    propertyDefinitions,
    recordId: 'new',
    resourceTypeId,
    fieldOptions: creationFieldOptions,
  })

  const editableFields = React.useMemo(
    () => fields.filter(field => !field.readOnly),
    [fields],
  )

  const formMethods = useDynamicForm(editableFields)
  useDeriveFieldUpdates(formMethods, resourceTypeId, editableFields)
  const fileUploadFn = useResourceFileUpload()

  const [isSaving, setIsSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)

  const loading = resourceSchema.loading

  const handleSave = React.useCallback(
    async (formData: Record<string, unknown>) => {
      if (!onCreateResource) return
      setIsSaving(true)
      setSaveError(null)

      const resolvedTemplateId = template?.id
      const allowedFieldNames = new Set(editableFields.map(f => f.name))
      const numberFieldNames = new Set(
        editableFields
          .filter(field => field.type === FieldType.NUMBER)
          .map(f => f.name),
      )
      const normalizedFormData = Object.entries(formData).reduce(
        (acc, [key, value]) => {
          if (!allowedFieldNames.has(key)) return acc
          if (
            numberFieldNames.has(key) &&
            value !== null &&
            value !== undefined &&
            value !== ''
          ) {
            const numericValue = Number(value)
            acc[key] = Number.isFinite(numericValue) ? numericValue : value
            return acc
          }
          acc[key] = value
          return acc
        },
        {} as Record<string, unknown>,
      )

      try {
        const newId = await onCreateResource({
          ...normalizedFormData,
          _templateId: resolvedTemplateId,
        })
        if (newId) {
          const newSelectedIds =
            mode === 'single' ? [newId] : Array.from(selectedIds).concat(newId)
          addCreatedItem(newId)
          stopCreating()
          setIsSaving(false)
          onSelect(newSelectedIds)
          return
        }
      } catch (error) {
        console.error(
          'Failed to create resource:',
          error instanceof Error ? error.message : error,
        )
        setSaveError(Localization.translator.failedToSaveChangesTryAgain)
      }
      setIsSaving(false)
    },
    [
      onCreateResource,
      template,
      editableFields,
      selectedIds,
      addCreatedItem,
      onSelect,
      stopCreating,
    ],
  )

  if (loading) {
    return <Loader />
  }

  if (needsSelection || isSwappingTemplate) {
    if (isCreatingNewForm) {
      return (
        <FormPropertiesSidebar
          mode="create"
          resourceTypeId={resourceTypeId}
          onClose={stopCreating}
          onBackToPicker={() => setIsCreatingNewForm(false)}
          resourceSchema={resourceSchema}
          onCreated={newTemplateId => {
            setIsCreatingNewForm(false)
            setIsSwappingTemplate(false)
            setChosenTemplateId(newTemplateId)
          }}
        />
      )
    }
    return (
      <FormListSelector
        title={getSelectFormTitle(resourceTypeId)}
        resourceTypeId={resourceTypeId}
        onSelect={id => {
          setChosenTemplateId(id)
          setIsSwappingTemplate(false)
        }}
        onClose={
          isSwappingTemplate ? () => setIsSwappingTemplate(false) : stopCreating
        }
        onCreateNewForm={
          isAdmin && templateAllowList == null
            ? () => setIsCreatingNewForm(true)
            : undefined
        }
        templates={templates}
        loading={templatesLoading}
      />
    )
  }

  return (
    <FormProvider {...formMethods}>
      <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:overflow-hidden">
        <ResourceEditFormSelector
          resourceTypeId={resourceTypeId}
          templates={templates}
          currentTemplateId={effectiveTemplateId}
          onClick={() => setIsSwappingTemplate(true)}
        />
        {editableFields.length > 0 && (
          <button
            type="button"
            className="tw:flex tw:items-center tw:gap-2 tw:w-full tw:h-10 tw:px-3 tw:bg-muted tw:border-0 tw:border-y tw:border-solid tw:border-border tw:cursor-pointer tw:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-inset tw:focus-visible:ring-ring"
            onClick={() => setIsPropertiesExpanded(prev => !prev)}
          >
            <ChevronDownIcon
              size="sm"
              className={`tw:text-muted-foreground tw:transition-transform tw:duration-200 ${
                isPropertiesExpanded ? '' : 'tw:-rotate-90'
              }`}
            />
            <span className="tw:text-title-sm tw:text-muted-foreground">
              {sectionLabel}
            </span>
          </button>
        )}
        {isPropertiesExpanded && editableFields.length > 0 && (
          <div className="tw:flex-1 tw:overflow-y-auto tw:px-4 tw:py-2">
            <DynamicForm
              fields={editableFields}
              onSubmit={handleSave}
              fileUploadFn={fileUploadFn}
              className="tw:flex tw:flex-col tw:gap-4"
              hideSubmitButton={true}
              hideHelperText={true}
              onFieldsChange={updateField}
            />
          </div>
        )}
        <div className="tw:flex tw:items-center tw:justify-between tw:p-2.5 tw:border-0 tw:border-t tw:border-solid tw:border-border">
          {saveError && (
            <div className="tw:text-destructive tw:text-body-sm tw:mr-2 tw:flex-1">
              {saveError}
            </div>
          )}
          {allowSelectExisting !== false ? (
            <Button
              variant="outline"
              onClick={stopCreating}
              disabled={isSaving}
              size="lg"
            >
              <ChecklistIcon data-icon="inline-start" size="xs" />
              {Localization.translator.select}
            </Button>
          ) : (
            <div />
          )}
          <div className="tw:flex tw:items-center tw:gap-2">
            <Button
              variant="outline"
              onClick={stopCreating}
              disabled={isSaving}
              size="lg"
            >
              {Localization.translator.cancel}
            </Button>
            <Button
              variant="default"
              render={<button type="submit" form={DYNAMIC_FORM_ID} />}
              disabled={isSaving}
              size="lg"
            >
              {isSaving && (
                <span data-icon="inline-start" className="tw:inline-flex">
                  <Spinner size="sm" variant="white" />
                </span>
              )}
              {Localization.translator.create}
            </Button>
          </div>
        </div>
      </div>
    </FormProvider>
  )
}

const PickerCreationPanel: React.FC = () => {
  const { resourceSchema } = useResourcePickerContext()
  if (!resourceSchema) return null
  return <PickerCreationForm resourceSchema={resourceSchema} />
}

export default PickerCreationPanel
